/**
 * Modal para elegir entre comprar solo una guía o suscribirse a Pro.
 * También se usa cuando el usuario alcanza el límite de guías de su plan.
 * Diseño alineado con Dashboard: tokens, tipografía y jerarquía visual.
 */
import React from "react";
import {
	Box,
	Typography,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Link,
	Checkbox,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import { BasicModal } from "./BasicModal/BasicModal";
import PackConfirmModal from "./PackConfirmModal";
import { LaunchCouponBanner } from "./LaunchCouponBanner";
import { LAUNCH_COUPON } from "../config/launchCoupon";
import { trackPurchaseAction } from "../utils/analytics";

const SUBSCRIPTION_PRICE = 29;
const SUBSCRIPTION_ORIGINAL_PRICE = 49;

/* Tokens alineados con Dashboard.css y ExploreSection */
const tokens = {
	primary: "#6366F1",
	primaryDark: "#4F46E5",
	textPrimary: "#1a1a2e",
	textMuted: "#64748B",
	textSecondary: "#4B5563",
	border: "rgba(0, 0, 0, 0.06)",
	radiusCard: "16px",
	radiusButton: "12px",
	shadowSm: "0 2px 8px rgba(0, 0, 0, 0.04)",
	shadowMd: "0 4px 16px rgba(0, 0, 0, 0.08)",
	shadowPrimary: "0 4px 16px rgba(99, 102, 241, 0.35)",
	bgSubtle: "rgba(0, 0, 0, 0.02)",
	primaryTint: "rgba(99, 102, 241, 0.08)",
};

export function PurchaseChoiceModal({
	open,
	guide = null,
	onClose,
	onBuyGuide,
	onSubscribe,
	onBuyPack,
	loading: externalLoading = false,
	limitInfo = null,
}) {
	const [showPackConfirm, setShowPackConfirm] = React.useState(false);
	const [acceptNoWithdrawal, setAcceptNoWithdrawal] = React.useState(false);
	const [showCheckError, setShowCheckError] = React.useState(false);
	const [buyLoading, setBuyLoading] = React.useState(false);
	const [subscribeLoading, setSubscribeLoading] = React.useState(false);
	const consentRef = React.useRef(null);

	React.useEffect(() => {
		if (!open) {
			setShowPackConfirm(false);
			setAcceptNoWithdrawal(false);
			setShowCheckError(false);
			setBuyLoading(false);
			setSubscribeLoading(false);
		}
	}, [open, guide?.id]);

	if (!guide) return null;

	const loading = externalLoading || buyLoading || subscribeLoading;
	const consentOk = acceptNoWithdrawal;

	const scrollToConsent = () => {
		consentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
	};

	const handleBuyGuide = async () => {
		if (!consentOk) {
			setShowCheckError(true);
			scrollToConsent();
			return;
		}
		trackPurchaseAction("buy_guide_clicked", {
			guide_id: guide?.id ? String(guide.id) : undefined,
			guide_name: guide?.name || guide?.id,
			price: guidePrice,
		});
		setBuyLoading(true);
		try {
			await onBuyGuide(guide);
		} catch {
			setBuyLoading(false);
		}
	};
	const handleSubscribeClick = async () => {
		if (!consentOk) {
			setShowCheckError(true);
			scrollToConsent();
			return;
		}
		trackPurchaseAction("subscribe_clicked", {
			guide_id: guide?.id ? String(guide.id) : undefined,
			guide_name: guide?.name || guide?.id,
			is_limit_reached: isLimitReached,
		});
		setSubscribeLoading(true);
		try {
			await onSubscribe();
		} catch {
			setSubscribeLoading(false);
		}
	};

	const guideName = guide.name || guide.id;
	const guidePrice = guide.price != null ? Number(guide.price) : 0;
	const priceLabel =
		guidePrice > 0
			? guidePrice % 1 === 0
				? `${guidePrice}€`
				: `${guidePrice.toFixed(2).replace(".", ",")}€`
			: "—";
	const hasCover = Boolean(guide.coverImage);
	const isLimitReached = Boolean(limitInfo);
	const packInfo = guide.requiresPack || null;
	const requiresPack = Boolean(packInfo);
	const packPriceLabel = (() => {
		if (!packInfo?.price) return "—";
		const p = Number(packInfo.price);
		return p % 1 === 0 ? `${p}€` : `${p.toFixed(2).replace(".", ",")}€`;
	})();
	const showBuyOption = !isLimitReached && guidePrice > 0 && !requiresPack;
	const showPackOption = !isLimitReached && requiresPack && !!packInfo;

	return (
		<>
		<BasicModal width={440} showModal={open} handleClose={onClose}>
			<style>{`@keyframes consent-shake { 0%, 100% { transform: translateX(0); } 20%, 60% { transform: translateX(-6px); } 40%, 80% { transform: translateX(6px); } }`}</style>
			<Box sx={{ pt: 0.5, pb: 1 }}>
				{/* Título */}
				<Typography
					variant="h6"
					sx={{
						fontWeight: 700,
						color: tokens.textPrimary,
						fontSize: "1.125rem",
						mb: isLimitReached ? 1 : 0.5,
					}}
				>
					{isLimitReached
						? "Has alcanzado el límite de tu plan"
						: "¿Cómo quieres acceder?"}
				</Typography>

			{/* Checkbox consentimiento expreso desistimiento (art. 103.m, no premarcado) */}
			<Box
				ref={consentRef}
				sx={{
					mb: 2,
					...(showCheckError && {
						border: "2px solid #d32f2f",
						borderRadius: tokens.radiusButton,
						bgcolor: "rgba(211, 47, 47, 0.08)",
						p: 0.75,
						animation: "consent-shake 0.5s ease-in-out",
					}),
				}}
			>
						<Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
							<Checkbox
								size="small"
								checked={acceptNoWithdrawal}
								onChange={(e) => {
									setAcceptNoWithdrawal(e.target.checked);
									setShowCheckError(false);
								}}
								sx={{
									color: showCheckError ? "#d32f2f" : tokens.textMuted,
									"&.Mui-checked": { color: tokens.primary },
									mt: 0.15,
									p: 0.25,
								}}
							/>
							<Typography
								variant="caption"
								sx={{
									color: tokens.textMuted,
									fontSize: "0.75rem",
									lineHeight: 1.4,
								}}
							>
								Entiendo que al acceder al contenido digital pierdo el derecho
								de desistimiento de 14 días{" "}
								<Link
									component={RouterLink}
									to="/legal/terminos"
									rel="nofollow"
									target="_blank"
									sx={{
										color: tokens.primary,
										textDecoration: "none",
										"&:hover": { textDecoration: "underline" },
									}}
								>
									(más info)
								</Link>
							</Typography>
						</Box>
				</Box>

				{/* Banner de límite alcanzado */}
				{isLimitReached && (
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							gap: 1,
							mb: 2,
							p: 1.5,
							borderRadius: tokens.radiusButton,
							bgcolor: "rgba(245, 158, 11, 0.08)",
							border: "1px solid rgba(245, 158, 11, 0.25)",
						}}
					>
						<InfoOutlinedIcon
							sx={{ color: "#D97706", fontSize: 20, mt: 0.1, flexShrink: 0 }}
						/>
						<Typography
							variant="body2"
							sx={{ color: tokens.textSecondary, lineHeight: 1.5 }}
						>
							Tu plan gratuito incluye{" "}
							<strong>
								{limitInfo.maxGuides} guía{limitInfo.maxGuides !== 1 ? "s" : ""}
							</strong>{" "}
							y ya{" "}
							{limitInfo.currentGuides === 1
								? "la has usado"
								: `has usado las ${limitInfo.currentGuides}`}
							. Hazte Pro para crear guías ilimitadas, personalizar las nuestras
							y acceder a todas las funciones.
						</Typography>
					</Box>
				)}

				{/* Contexto de la guía */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						mb: 2,
						p: 1.25,
						borderRadius: tokens.radiusButton,
						bgcolor: tokens.bgSubtle,
						border: `1px solid ${tokens.border}`,
					}}
				>
					{hasCover && (
						<Box
							component="img"
							src={guide.coverImage}
							alt=""
							sx={{
								width: 40,
								height: 40,
								borderRadius: 1,
								objectFit: "cover",
							}}
						/>
					)}
					<Typography
						variant="body2"
						sx={{
							fontWeight: 600,
							color: tokens.textPrimary,
							flex: 1,
						}}
					>
						{guideName}
					</Typography>
				</Box>

				{/* Opción pack: esta guía sólo se vende como parte de un pack */}
				{showPackOption && (
					<Card
						elevation={0}
						sx={{
							mb: 1.5,
							borderRadius: tokens.radiusCard,
							border: "1px solid rgba(239, 68, 68, 0.35)",
							boxShadow: "0 6px 20px -10px rgba(239, 68, 68, 0.4)",
							overflow: "hidden",
							background:
								"linear-gradient(135deg, rgba(239,68,68,0.06) 0%, rgba(239,68,68,0.02) 100%)",
						}}
					>
						<CardContent
							sx={{ py: 1.5, px: 1.75, "&:last-child": { pb: 1.5 } }}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "flex-start",
									gap: 1.25,
									mb: 1.25,
								}}
							>
								<Box
									sx={{
										width: 36,
										height: 36,
										borderRadius: "10px",
										bgcolor: "rgba(239, 68, 68, 0.14)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<CardGiftcardRoundedIcon
										sx={{ color: "#dc2626", fontSize: 20 }}
									/>
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.75,
											flexWrap: "wrap",
										}}
									>
										<Typography
											variant="subtitle2"
											sx={{ fontWeight: 700, color: tokens.textPrimary }}
										>
											{packInfo.title || "Pack"}
										</Typography>
										<Chip
											label="Recomendado"
											size="small"
											sx={{
												height: 20,
												fontSize: "0.7rem",
												fontWeight: 600,
												bgcolor: "#dc2626",
												color: "#fff",
												"& .MuiChip-label": { px: 0.75 },
											}}
										/>
									</Box>
									<Typography
										variant="body2"
										sx={{ color: tokens.textMuted, mt: 0.25 }}
									>
										{packInfo.subtitle ||
											"Guía completa + extras. Pago único."}
									</Typography>
								</Box>
							</Box>
						<Button
							variant="contained"
							fullWidth
							disabled={loading || !onBuyPack}
							onClick={() => {
								if (!consentOk) {
									setShowCheckError(true);
									scrollToConsent();
									return;
								}
								setShowPackConfirm(true);
							}}
							startIcon={
									loading ? (
										<CircularProgress size={18} color="inherit" />
									) : (
										<CardGiftcardRoundedIcon />
									)
								}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									py: 1.25,
									borderRadius: tokens.radiusButton,
									background:
										"linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
									"&:hover": {
										background:
											"linear-gradient(135deg, #f87171 0%, #ef4444 100%)",
										boxShadow: "0 4px 14px rgba(239, 68, 68, 0.4)",
									},
								}}
							>
								Conseguir pack — {packPriceLabel}
							</Button>
						</CardContent>
					</Card>
				)}

				{/* Opción 1: Solo esta guía (solo si tiene precio y no es límite) */}
				{showBuyOption && (
					<Card
						elevation={0}
						sx={{
							mb: 1.5,
							borderRadius: tokens.radiusCard,
							border: `1px solid ${tokens.border}`,
							boxShadow: tokens.shadowSm,
							overflow: "hidden",
							transition: "border-color 0.2s ease, box-shadow 0.2s ease",
							"&:hover": {
								borderColor: "rgba(245, 158, 11, 0.4)",
								boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
							},
						}}
					>
						<CardContent
							sx={{ py: 1.5, px: 1.75, "&:last-child": { pb: 1.5 } }}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "flex-start",
									gap: 1.25,
									mb: 1.25,
								}}
							>
								<Box
									sx={{
										width: 36,
										height: 36,
										borderRadius: "10px",
										bgcolor: "rgba(245, 158, 11, 0.12)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<ShoppingCartIcon sx={{ color: "#D97706", fontSize: 20 }} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 700, color: tokens.textPrimary }}
									>
										Solo esta guía
									</Typography>
									<Typography
										variant="body2"
										sx={{ color: tokens.textMuted, mt: 0.25 }}
									>
										Accede únicamente a poder ver la guía de {guideName}. Pago único. No podrás crear nuevas guías con ello.
									</Typography>
								</Box>
							</Box>
							<Button
								variant="contained"
								fullWidth
								disabled={loading}
								onClick={handleBuyGuide}
								startIcon={
									buyLoading ? (
										<CircularProgress size={18} color="inherit" />
									) : (
										<ShoppingCartIcon />
									)
								}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									py: 1.25,
									borderRadius: tokens.radiusButton,
									background:
										"linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
									"&:hover": {
										background:
											"linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)",
										boxShadow: "0 4px 14px rgba(245, 158, 11, 0.4)",
									},
								}}
							>
								Pagar y acceder ahora — {priceLabel}
							</Button>
						</CardContent>
					</Card>
				)}

				{/* Opción 2: Pro */}
				<Card
					elevation={0}
					sx={{
						mb: 1.5,
						borderRadius: tokens.radiusCard,
						border: `2px solid ${tokens.primary}`,
						boxShadow: tokens.shadowPrimary,
						overflow: "hidden",
						bgcolor: tokens.primaryTint,
						transition: "box-shadow 0.2s ease",
						"&:hover": {
							boxShadow: "0 6px 20px rgba(99, 102, 241, 0.4)",
						},
					}}
				>
					<CardContent sx={{ py: 1.5, px: 1.75, "&:last-child": { pb: 1.5 } }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-start",
								gap: 1.25,
								mb: 1,
							}}
						>
							<Box
								sx={{
									width: 36,
									height: 36,
									borderRadius: "10px",
									bgcolor: "rgba(99, 102, 241, 0.2)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<AutoAwesomeIcon sx={{ color: tokens.primary, fontSize: 20 }} />
							</Box>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.75,
										flexWrap: "wrap",
									}}
								>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 700, color: tokens.textPrimary }}
									>
										Pro
									</Typography>
									<Chip
										label="Mejor valor"
										size="small"
										sx={{
											height: 20,
											fontSize: "0.7rem",
											fontWeight: 600,
											bgcolor: tokens.primary,
											color: "#fff",
											"& .MuiChip-label": { px: 0.75 },
										}}
									/>
								</Box>
								<Typography
									variant="body2"
									sx={{ color: tokens.textMuted, mt: 0.25 }}
								>
									Todas las guías incluidas y creación sin límites. Plan anual.
								</Typography>
								{LAUNCH_COUPON.show && (
									<LaunchCouponBanner compact sx={{ mt: 1 }} />
								)}
								<Box
									sx={{
										display: "inline-flex",
										alignItems: "center",
										gap: 1,
										mt: 0.75,
										px: 1.25,
										py: 0.5,
										borderRadius: "8px",
										bgcolor: "rgba(99, 102, 241, 0.08)",
									}}
								>
									<Typography
										sx={{
											fontSize: "0.8rem",
											color: tokens.textMuted,
											textDecoration: "line-through",
										}}
									>
										{SUBSCRIPTION_ORIGINAL_PRICE}€/año
									</Typography>
									<Typography
										sx={{
											fontSize: "0.95rem",
											fontWeight: 800,
											color: tokens.primary,
										}}
									>
										{SUBSCRIPTION_PRICE}€/año
									</Typography>
									<Chip
										label="Con cupón LANZAMIENTO"
										size="small"
										sx={{
											height: 18,
											fontSize: "0.65rem",
											fontWeight: 700,
											bgcolor: "#10B981",
											color: "#fff",
											"& .MuiChip-label": { px: 0.75 },
										}}
									/>
								</Box>
								<Typography
									variant="caption"
									sx={{
										display: "block",
										mt: 0.5,
										color: tokens.textMuted,
										fontSize: "0.7rem",
										lineHeight: 1.3,
									}}
								>
									Usa el cupón <strong>LANZAMIENTO</strong> en el pago para obtener el descuento.
								</Typography>
								<Box
									component="ul"
									sx={{
										m: 0,
										mt: 0.75,
										pl: 1.5,
										listStyle: "none",
										display: "flex",
										flexDirection: "column",
										gap: 0.25,
									}}
								>
									{[
										"Todas las guías incluidas",
										"Crea nuevas guías desde cero",
										"Personaliza nuestras guías",
										"Acceso completo a toda la plataforma",
									].map((item) => (
										<Box
											key={item}
											component="li"
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												fontSize: "0.8125rem",
												color: tokens.textSecondary,
											}}
										>
											<CheckCircleOutlineIcon
												sx={{ fontSize: 16, color: tokens.primary }}
											/>
											{item}
										</Box>
									))}
								</Box>
							</Box>
						</Box>
						<Button
							variant="contained"
							fullWidth
							disabled={loading}
							onClick={handleSubscribeClick}
							startIcon={
								subscribeLoading ? (
									<CircularProgress size={18} color="inherit" />
								) : (
									<AutoAwesomeIcon />
								)
							}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								py: 1.25,
								borderRadius: tokens.radiusButton,
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
									boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
								},
							}}
						>
							<Box component="span" sx={{ display: "inline" }}>
								Pagar y acceder ahora —{" "}
								{LAUNCH_COUPON.show ? (
									<>
										<Box
											component="span"
											sx={{
												textDecoration: "line-through",
												opacity: 0.85,
												mr: 0.5,
											}}
										>
											{LAUNCH_COUPON.subscriptionPriceEur.toFixed(2).replace(".", ",")}€/año
										</Box>
										{LAUNCH_COUPON.priceAfterDiscount.toFixed(2).replace(".", ",")}€/año con cupón
									</>
								) : (
									`${SUBSCRIPTION_ORIGINAL_PRICE}€/año`
								)}
							</Box>
						</Button>
					</CardContent>
				</Card>

				{/* Cancelar */}
				<Button
					variant="text"
					fullWidth
					disabled={loading}
					onClick={onClose}
					sx={{
						textTransform: "none",
						color: tokens.textMuted,
						fontSize: "0.875rem",
						mt: 0.5,
						"&:hover": { bgcolor: tokens.bgSubtle, color: tokens.textPrimary },
					}}
				>
					Cancelar
				</Button>
			</Box>
		</BasicModal>

		{showPackOption && (
			<PackConfirmModal
				open={showPackConfirm}
				onClose={() => setShowPackConfirm(false)}
				onConfirm={() => {
					setShowPackConfirm(false);
					onBuyPack?.(packInfo, guide);
				}}
				loading={loading}
				packTitle={packInfo?.title || "Pack"}
				packPrice={packPriceLabel}
			/>
		)}
		</>
	);
}
